import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface SubscriptionPlan {
  id: string;
  plan_name: string;
  plan_type: string;
  price_vatu: number;
  billing_model?: 'flat' | 'per_member';
  price_per_member_vatu?: number | null;
  max_group_members: number | null;
  max_contributions_per_month: number | null;
  features: any;
}

interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlanId: string | null;
  currentPlanName: string;
  groupId: string;
  userId: string;
  actorRole?: 'super_admin' | 'group_admin';
  onUpgradeSuccess: () => void;
}

const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({
  isOpen,
  onClose,
  currentPlanId,
  currentPlanName,
  groupId,
  userId,
  actorRole = 'group_admin',
  onUpgradeSuccess,
}) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const endOfMonthIso = (): string => {
    const now = new Date();
    // End of current month in UTC
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
    return end.toISOString();
  };

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlans();
    }
  }, [isOpen, currentPlanId]);

  const fetchAvailablePlans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all active plans
      const { data, error: fetchError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_vatu', { ascending: true });

      if (fetchError) throw fetchError;

      // Filter out the current plan and show only higher-tier plans
      const availablePlans = (data || []).filter((plan) => {
        // If current plan is free, show all paid plans
        if (!currentPlanId) return plan.plan_type !== 'free';
        // Otherwise, show plans that are different from current
        return plan.id !== currentPlanId;
      });

      setPlans(availablePlans);
    } catch (err: any) {
      console.error('Error fetching plans:', err);
      setError(err.message || 'Failed to load available plans');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedPlanId) {
      setError('Please select a plan to upgrade to');
      return;
    }

    try {
      setUpgrading(true);
      setError(null);

      const selectedPlan = plans.find((p) => p.id === selectedPlanId);
      if (!selectedPlan) {
        throw new Error('Selected plan not found');
      }
      const isPayAsYouGo = selectedPlan.billing_model === 'per_member';

      // Update group's subscription plan
      const { data: updateResult, error: groupError } = await supabase.rpc(
        'set_group_subscription_plan',
        { group_id_param: groupId, plan_id_param: selectedPlanId }
      );
      if (groupError) throw groupError;
      if (updateResult?.success !== true) {
        throw new Error(updateResult?.error || 'Failed to update group plan');
      }

      // Get or create user subscription
      const { data: existingSubscription } = await supabase
        .from('user_subscriptions')
        .select('id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (selectedPlan.plan_type === 'free') {
        // For free plans, auto-activate
        if (existingSubscription) {
          const { error: updateError } = await supabase
            .from('user_subscriptions')
            .update({
              plan_id: selectedPlanId,
              subscription_status: 'active',
              access_locked: false,
              payment_due_date: null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingSubscription.id);

          if (updateError) throw updateError;
        } else {
          // Create new subscription for free plan
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + 30); // 30 days default

          const { error: createError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: userId,
              plan_id: selectedPlanId,
              subscription_status: 'active',
              access_locked: false,
              start_date: new Date().toISOString(),
              end_date: endDate.toISOString(),
            });

          if (createError) throw createError;
        }
      } else {
        // For paid plans:
        // - per_member (pay-as-you-go): activate immediately, payment due end-of-month
        // - other paid plans: keep existing behavior (pending + payment flow)
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + 30); // keep existing 30-day window for end_date
        const dueDateIso = endOfMonthIso();

        if (existingSubscription) {
          const { error: updateError } = await supabase
            .from('user_subscriptions')
            .update({
              plan_id: selectedPlanId,
              subscription_status: isPayAsYouGo ? 'active' : 'pending',
              access_locked: false,
              start_date: new Date().toISOString(),
              end_date: endDate.toISOString(),
              payment_due_date: isPayAsYouGo ? dueDateIso : null,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existingSubscription.id);

          if (updateError) throw updateError;
        } else {
          const { error: createError } = await supabase
            .from('user_subscriptions')
            .insert({
              user_id: userId,
              plan_id: selectedPlanId,
              subscription_status: isPayAsYouGo ? 'active' : 'pending',
              access_locked: false,
              start_date: new Date().toISOString(),
              end_date: endDate.toISOString(),
              payment_due_date: isPayAsYouGo ? dueDateIso : null,
            });

          if (createError) throw createError;
        }

        // Initialize payment for non-pay-as-you-go plans.
        // Pay-as-you-go amount is calculated later (per group / member count), and payment is due end-of-month.
        if (!isPayAsYouGo) {
          const { error: paymentError } = await supabase.rpc('initialize_subscription_payment', {
            user_id_param: userId,
            group_id_param: groupId,
          });

          if (paymentError) {
            console.error('Error initializing payment:', paymentError);
            // Don't throw - payment can be initialized later
          }
        }

        // Close + refresh
        onUpgradeSuccess();
        onClose();

        // For non-pay-as-you-go plans, group_admin goes to payment instructions.
        if (!isPayAsYouGo && actorRole !== 'super_admin') {
          // Navigate to payment instructions page
          navigate('/payment-instructions', {
            state: {
              planId: selectedPlanId,
              groupId: groupId,
              upgrade: true,
            },
          });
          return;
        }
      }

      // For free plans, just refresh
      onUpgradeSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error upgrading plan:', err);
      setError(err.message || 'Failed to upgrade plan. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const formatFeatures = (features: any): string[] => {
    if (!features) return [];
    if (Array.isArray(features)) return features;
    if (typeof features === 'object') {
      if (features.features && Array.isArray(features.features)) {
        return features.features;
      }
      return Object.values(features).filter((v) => typeof v === 'string') as string[];
    }
    return [];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200">
      <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="ArrowUp" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Upgrade Plan</h3>
            <p className="text-sm text-muted-foreground">
              Select a plan to upgrade to. Free plans activate immediately. Paid plans require payment approval.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-interactive"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="mb-4 p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground">
              Current Plan: <span className="font-medium text-foreground">{currentPlanName}</span>
            </p>
          </div>

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading available plans...</div>
        ) : plans.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No upgrade plans available
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {plans.map((plan) => {
              const features = formatFeatures(plan.features);
              const isSelected = selectedPlanId === plan.id;

              return (
                <div
                  key={plan.id}
                  onClick={() => setSelectedPlanId(plan.id)}
                  className={`p-4 border rounded-md cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-foreground">{plan.plan_name}</h4>
                        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded capitalize">
                          {plan.plan_type}
                        </span>
                      </div>
                      <div className="text-lg font-bold text-foreground mb-2">
                        {plan.price_vatu > 0 ? `${Number(plan.price_vatu).toLocaleString()} VT` : 'Free'}
                        {plan.price_vatu > 0 && <span className="text-sm font-normal text-muted-foreground">/month</span>}
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        {plan.max_group_members && (
                          <div>• Up to {plan.max_group_members} members</div>
                        )}
                        {plan.max_contributions_per_month && (
                          <div>• {plan.max_contributions_per_month} contributions/month</div>
                        )}
                        {features.length > 0 && (
                          <div className="mt-2">
                            {features.slice(0, 3).map((feature: string, idx: number) => (
                              <div key={idx}>• {feature}</div>
                            ))}
                            {features.length > 3 && (
                              <div className="text-xs">+ {features.length - 3} more features</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      {isSelected ? (
                        <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Icon name="Check" size={14} color="white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-border" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={upgrading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleUpgrade}
            loading={upgrading}
            disabled={!selectedPlanId || upgrading}
            iconName="ArrowUp"
            iconPosition="right"
          >
            Upgrade Plan
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePlanModal;

